import { Client } from "@modelcontextprotocol/sdk/client/index.js";
import { CallToolResult, GetPromptResult, Prompt, ReadResourceResult, Resource, Tool } from "@modelcontextprotocol/sdk/types.js";
import { StreamableHTTPClientTransport } from "@modelcontextprotocol/sdk/client/streamableHttp.js";
import { getAuthToken } from "services/AccountServices";
import { circuitBreaker } from "circuitbreaker/CircuitBreaker";
import { mcpConfigService, MCPServerConfig } from "mcp/services/MCPConfigService";

export interface MCPClientState {
    isCindraConnected: boolean;
    isConnecting: boolean;
    error: string | null;
    tools: Tool[];
    resources: Resource[];
    prompts: Prompt[];
    lastConnectionAttempt?: Date;
    reconnectAttempts: number;
    isCindraEnabled: boolean;
}

// Tools that don't require the auth_token argument
const TOOLS_WITHOUT_TOKEN = new Set([
    "server_status"
]);

export class MCPClientService {
    private client: Client | null = null;
    private transport: StreamableHTTPClientTransport | null = null;
    private listeners: Set<(state: MCPClientState) => void> = new Set();
    private currentConfig: MCPServerConfig | null = null;
    private reconnectInterval: ReturnType<typeof setInterval> | null = null;
    private healthCheckInterval: ReturnType<typeof setInterval> | null = null;
    private reconnectDelayMs = 5000; // 5 seconds
    private healthCheckDelayMs = 10000; // 10 seconds
    private isReconnecting = false;

    private state: MCPClientState = {
        isCindraConnected: false,
        isConnecting: false,
        error: null,
        tools: [],
        resources: [],
        prompts: [],
        reconnectAttempts: 0,
        isCindraEnabled: this.isCindraEnabled()
    };

    constructor() {
        if (this.isCindraEnabled()) {
            this.startHealthCheck();
        }
        else {
            console.log("[MCPClientService] Cindra is disabled via VITE_CINDRA_ENABLED environment variable");
        }
    }

    // ---=== Private Methods ===--- //

    private isCindraEnabled(): boolean {
        // In React/Vite, environment variables are available via import.meta.env and prefixed with VITE_
        const cindraEnabled = import.meta.env.VITE_CINDRA_ENABLED;
        return cindraEnabled === "true";
    }

    private getDefaultConfig(): MCPServerConfig | null {
        if (!this.isCindraEnabled()) {
            return null;
        }

        try {
            const configs = mcpConfigService.getServerConfigs();
            if (configs.length > 0) {
                const defaultConfig = configs[0]; // Use first config as default
                console.log(`[MCPClientService] Using default MCP server: ${defaultConfig.name} at ${defaultConfig.url}`);
                return defaultConfig;
            }
            else {
                console.log("[MCPClientService] No MCP server configurations available");
                return null;
            }
        }
        catch (error) {
            console.error("[MCPClientService] Error loading default MCP config:", error);
            return null;
        }
    }

    private async loadCapabilities(): Promise<void> {
        if (!this.client) return;

        try {
            // Load tools
            const toolsResponse = await this.client.listTools();
            const tools = toolsResponse.tools || [];

            // Load resources
            const resourcesResponse = await this.client.listResources();
            const resources = resourcesResponse.resources || [];

            // Load prompts
            const promptsResponse = await this.client.listPrompts();
            const prompts = promptsResponse.prompts || [];

            this.updateState({ tools, resources, prompts });
        }
        catch (error) {
            console.error("Failed to load capabilities:", error);
            this.updateState({
                error: error instanceof Error ? error.message : "Failed to load capabilities"
            });
        }
    }

    private convertArgsToStrings(args?: Record<string, unknown>): Record<string, string> {
        if (!args) return {};

        const stringArgs: Record<string, string> = {};
        for (const [key, value] of Object.entries(args)) {
            if (typeof value === "string") {
                stringArgs[key] = value;
            }
            else {
                // Convert non-string values to JSON strings
                stringArgs[key] = JSON.stringify(value);
            }
        }
        return stringArgs;
    }

    private notifyListeners() {
        this.listeners.forEach(listener => listener(this.state));
    }

    private updateState(updates: Partial<MCPClientState>) {
        this.state = { ...this.state, ...updates };
        this.notifyListeners();
    }

    private startHealthCheck() {
        // Clear existing health check
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
        }

        this.healthCheckInterval = setInterval(async () => {
            if (this.state.isCindraConnected && !this.state.isConnecting && this.isCindraEnabled()) {
                try {
                    // Try to call a simple tool to check if the server is still responsive
                    await this.callTool("server_status");
                }
                catch (error) {
                    console.warn("[MCPClient] Server status check failed, server may be disconnected:", error);
                    await this.handleDisconnection(error instanceof Error ? error.message : "Server status check failed");
                }
            }
        }, this.healthCheckDelayMs);
    }

    private async handleDisconnection(errorMessage: string) {
        console.log("[MCPClient] Handling disconnection:", errorMessage);

        // Update state to reflect disconnection
        this.updateState({
            isCindraConnected: false,
            error: errorMessage,
            tools: [],
            resources: [],
            prompts: []
        });

        // Clean up current connection
        await this.cleanupConnection();

        // Start reconnection attempts if we have a config to reconnect to
        if (this.currentConfig) {
            this.startReconnectionAttempts();
        }
    }

    private startReconnectionAttempts() {
        if (this.reconnectInterval || this.isReconnecting) {
            return; // Already attempting reconnection
        }

        console.log("[MCPClient] Starting unlimited reconnection attempts");
        this.isReconnecting = true;

        this.reconnectInterval = setInterval(async () => {
            if (!this.state.isConnecting && !this.state.isCindraConnected && this.currentConfig) {
                console.log(`[MCPClient] Reconnection attempt ${this.state.reconnectAttempts + 1}`);

                this.updateState({
                    reconnectAttempts: this.state.reconnectAttempts + 1,
                    lastConnectionAttempt: new Date()
                });

                try {
                    await this.internalConnect(this.currentConfig, true);

                    if (this.state.isCindraConnected) {
                        console.log("[MCPClient] Reconnection successful");

                        // Signal circuit breaker that connectivity has been restored
                        // Note: We don't have access to NotificationManager here, but the CircuitBreaker
                        // will use the global notification manager when recordSuccess is called from API calls
                        console.log("[MCPClient] Signaling circuit breaker that connectivity has been restored");
                        circuitBreaker.recordSuccess();

                        this.stopReconnectionAttempts();
                        this.updateState({ reconnectAttempts: 0 });
                    }
                }
                catch (reconnectError) {
                    console.warn("[MCPClient] Reconnection attempt failed:", reconnectError);
                }
            }
        }, this.reconnectDelayMs);
    }

    private stopReconnectionAttempts() {
        if (this.reconnectInterval) {
            clearInterval(this.reconnectInterval);
            this.reconnectInterval = null;
        }
        this.isReconnecting = false;
    }

    private async internalConnect(config: MCPServerConfig, isReconnect: boolean = false): Promise<void> {
        if (!this.isCindraEnabled()) {
            throw new Error("MCPClientService is disabled. Set VITE_CINDRA_ENABLED=true to enable.");
        }

        if (this.state.isConnecting) {
            return;
        }

        // If we're already connected to the same server, don't reconnect
        if (this.state.isCindraConnected && this.currentConfig?.url === config.url && !isReconnect) {
            return;
        }

        try {
            this.updateState({
                isConnecting: true,
                error: null,
                lastConnectionAttempt: new Date()
            });

            // Clean up any existing connection
            await this.cleanupConnection();

            // Store current config for reconnection attempts
            this.currentConfig = config;

            // Create transport
            this.transport = new StreamableHTTPClientTransport(
                new URL(config.url),
                {
                    requestInit: {
                        headers: {
                            "X-User-Agent": "KarunaPORTAL (MCPClientService)"
                        }
                    }
                }
            );

            // Create client
            this.client = new Client({
                name: "karuna-mcp-client",
                version: "0.0.1"
            }, {
                capabilities: {
                    tools: {},
                    resources: {},
                    prompts: {}
                }
            });

            // Connect client to transport
            await this.client.connect(this.transport);

            // Load available tools, resources, and prompts
            await this.loadCapabilities();

            this.updateState({
                isCindraConnected: true,
                isConnecting: false,
                error: null
            });

            // If this is a reconnection, signal circuit breaker that connectivity has been restored
            if (isReconnect) {
                console.log("[MCPClient] Reconnection established - signaling circuit breaker");
                circuitBreaker.recordSuccess();
            }

        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            console.error("[MCPClient] Connection failed:", errorMessage);

            this.updateState({
                isCindraConnected: false,
                isConnecting: false,
                error: errorMessage
            });

            // Clean up failed connection
            await this.cleanupConnection();
            throw error;
        }
    }

    private async cleanupConnection(): Promise<void> {
        if (this.client) {
            try {
                await this.client.close();
            }
            catch (error) {
                console.warn("[MCPClient] Error closing client:", error);
            }
            this.client = null;
        }

        if (this.transport) {
            try {
                await this.transport.close();
            }
            catch (error) {
                console.warn("[MCPClient] Error closing transport:", error);
            }
            this.transport = null;
        }
    }

    // ---=== Public API ===--- //

    public isEnabled(): boolean {
        return this.isCindraEnabled();
    }

    public getState(): MCPClientState {
        return this.state;
    }

    public subscribe(listener: (state: MCPClientState) => void): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    public async connect(config?: MCPServerConfig): Promise<void> {
        if (!this.isCindraEnabled()) {
            console.log("[MCPClientService] Connect called but Cindra is disabled");
            return; // Gracefully return without throwing
        }

        // Stop any ongoing reconnection attempts when manually connecting
        this.stopReconnectionAttempts();
        this.updateState({ reconnectAttempts: 0 });

        // Use provided config or load default
        let configToUse = config;
        if (!configToUse) {
            configToUse = this.getDefaultConfig();
            if (!configToUse) {
                throw new Error("No MCP server configuration available and none provided");
            }
        }

        console.log(`[MCPClientService] Attempting to connect to MCP server: ${configToUse.name} at ${configToUse.url}`);

        try {
            // Use the existing internalConnect method
            await this.internalConnect(configToUse, false);
            console.log(`[MCPClientService] Connected to MCP server: ${configToUse.name} at ${configToUse.url}`);
        }
        catch (error) {
            console.error(`[MCPClientService] Initial connection failed: ${error}`);

            // Store the config for reconnection attempts even if initial connection fails
            this.currentConfig = configToUse;

            // Start reconnection attempts for failed initial connection
            this.startReconnectionAttempts();

            // Don't throw the error - let the reconnection process handle it
            console.log("[MCPClientService] Starting unlimited reconnection attempts after initial connection failure");
        }
    }

    public async forceReconnect(): Promise<void> {
        if (!this.isCindraEnabled()) {
            console.log("[MCPClientService] Force reconnect called but Cindra is disabled");
            return; // Gracefully return without throwing
        }

        if (this.currentConfig) {
            console.log("[MCPClient] Force reconnect initiated");

            // Stop current reconnection attempts
            this.stopReconnectionAttempts();
            this.updateState({ reconnectAttempts: 0 });

            try {
                await this.internalConnect(this.currentConfig, true);
                console.log("[MCPClient] Force reconnect successful");
            }
            catch (error) {
                console.warn("[MCPClient] Force reconnect failed, restarting retry cycle:", error);
                // Restart reconnection attempts if force reconnect fails
                if (this.currentConfig) {
                    this.startReconnectionAttempts();
                }
                throw error;
            }
        }
    }

    public async disconnect(): Promise<void> {
        if (!this.isCindraEnabled()) {
            console.log("[MCPClientService] Disconnect called but Cindra is disabled");
            return; // Gracefully return without throwing
        }

        try {
            // Stop reconnection attempts
            this.stopReconnectionAttempts();

            // Clear current config so we don't try to reconnect
            this.currentConfig = null;

            await this.cleanupConnection();

            this.updateState({
                isCindraConnected: false,
                isConnecting: false,
                error: null,
                tools: [],
                resources: [],
                prompts: [],
                reconnectAttempts: 0
            });
        }
        catch (error) {
            this.updateState({
                error: error instanceof Error ? error.message : "Error during disconnect"
            });
        }
    }

    public async callTool(name: string, arguments_?: any): Promise<CallToolResult> {
        if (!this.isCindraEnabled()) {
            throw new Error("MCPClientService is disabled. Set VITE_CINDRA_ENABLED=true to enable.");
        }

        if (!this.client) {
            throw new Error("MCP client is not connected");
        }

        try {
            let finalArguments = arguments_ || {};

            // Add auth_token as first argument for tools that require it
            if (!TOOLS_WITHOUT_TOKEN.has(name)) {
                const auth_token = getAuthToken();
                if (!auth_token) {
                    throw new Error("No authentication token available for tool call");
                }

                // Create new arguments object with auth_token as first argument
                finalArguments = {
                    auth_token: auth_token,
                    ...finalArguments
                };
            }

            return await this.client.callTool({
                name,
                arguments: finalArguments
            });
        }
        catch (error) {
            console.error(`[MCPClientService] Tool call failed: ${name}`, error);
            throw error;
        }
    }

    public async readResource(uri: string): Promise<ReadResourceResult> {
        if (!this.isCindraEnabled()) {
            throw new Error("MCPClientService is disabled. Set VITE_CINDRA_ENABLED=true to enable.");
        }

        if (!this.client || !this.state.isCindraConnected) {
            throw new Error("MCP client is not connected");
        }

        try {
            return await this.client.readResource({ uri });
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Resource read failed";

            // Check if this looks like a connection error
            if (errorMessage.includes("fetch") || errorMessage.includes("network") || errorMessage.includes("ECONNREFUSED")) {
                await this.handleDisconnection(errorMessage);
            }
            else {
                this.updateState({ error: errorMessage });
            }

            throw new Error(errorMessage);
        }
    }

    public async getPrompt(name: string, args?: Record<string, unknown>): Promise<GetPromptResult> {
        if (!this.isCindraEnabled()) {
            throw new Error("MCPClientService is disabled. Set VITE_CINDRA_ENABLED=true to enable.");
        }

        if (!this.client || !this.state.isCindraConnected) {
            throw new Error("MCP client is not connected");
        }

        try {
            return await this.client.getPrompt({
                name,
                arguments: this.convertArgsToStrings(args)
            });
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Prompt get failed";

            // Check if this looks like a connection error
            if (errorMessage.includes("fetch") || errorMessage.includes("network") || errorMessage.includes("ECONNREFUSED")) {
                await this.handleDisconnection(errorMessage);
            }
            else {
                this.updateState({ error: errorMessage });
            }

            throw new Error(errorMessage);
        }
    }

    // Cleanup method
    public destroy(): void {
        this.stopReconnectionAttempts();
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
            this.healthCheckInterval = null;
        }
        if (this.isCindraEnabled()) {
            void this.disconnect();
        }
    }
}

// Singleton instance
export const mcpClientService = new MCPClientService();
